import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { PlusIcon } from "./icons";
import { TkticDateInput } from "@/components/ui/TkticDateInput";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { ToneChipGroup } from "./ToneChipGroup";
import type { Player } from "@/lib/types";
import { ReferenceVideoField } from "./ReferenceVideoField";
import { useCollection } from "@/features/collections/useCollection";
import {
  CollectionPagination,
  CollectionToolbar,
} from "@/features/collections/CollectionControls";
import {
  NO_RECIPIENTS_WARNING,
  sharedWithSentence,
  visibilitySegmentOptions,
} from "@/features/coach/visibilityVocabulary";

/**
 * Composer form — the side-panel form for authoring a note or
 * observation in Coach Review.
 *
 * Field set matches the structured-feedback fields the backend's
 * `CreateCoachingNoteRequest` accepts. Mirror — not deeper than —
 * the Pydantic schema:
 *
 *   - title              (required)
 *   - note_type          (closed enum)
 *   - category           (closed enum; defaults to "shape")
 *   - visibility         (CoachingVisibility, defaults to "team")
 *   - body               (long-form free notes; backend max 4000)
 *   - player_summary     (player-facing line)
 *   - what_happened      (observation)
 *   - why_it_matters     (context)
 *   - what_to_do_next    (actionable)
 *   - coach_private_note (coach-only; defaults to "")
 *   - player_ids[]       (tagged players, multi-select)
 *   - tags[]             (free tags, parsed from comma-separated input)
 *   - event_title/date/type (observation mode only)
 *
 * Match-specific + observation-specific fields (timestamp_seconds,
 * event_date, etc.) are owned by the parent shell — the composer is
 * the structured-text part only. The shell composes the final payload
 * by merging the form values with the canvas data
 * (`tactical_board_json` / `drawing`) at submit time.
 *
 * Save action is provided by the parent via `onSubmit` — the
 * composer doesn't know which endpoint it's saving to. In M4 the
 * parent always saves a coaching NOTE (video-context when a match
 * is in scope, observation-context otherwise).
 *
 * Save Clip is NOT part of M4 and is intentionally absent here.
 * Clip authoring needs the M5 HLS video player: a clip is a
 * `[start_seconds, end_seconds]` window and those bounds come from
 * the playing video's `currentTime` (legacy pre-filled
 * `[currentTime − 5s, currentTime + 8s]`). M4 has no player, so a
 * Save Clip control would have nothing to read bounds from. M5
 * adds `useSaveCoachingClip` + a clip mode here once the player
 * lands — the `onSubmit`-injection design already supports it
 * without restructuring this component.
 *
 * Note on the "Structured feedback" disclosure: implemented as a
 * controlled state toggle (NOT a native `<details>` element). The
 * project's auto-memory rule disallows native browser chrome in
 * styled UIs, and `<details>`/`<summary>` has cross-browser marker
 * inconsistencies that the linter actively rejects. Using a plain
 * button + conditional render keeps the chrome 100% styled.
 */

// Note category — a CLOSED enum, not a free string. Mirrors the
// backend's `_VALID_NOTE_CATEGORIES` set in `models.py` (the
// `CreateCoachingNoteRequest.validate_category` validator 422s
// anything outside it). The template registry only ever uses
// values from this set, so applying a template can't desync the
// dropdown. Keep this list in lockstep with `_VALID_NOTE_CATEGORIES`.
export const NOTE_CATEGORIES = [
  "shape",
  "pressing",
  "transition",
  "set_piece",
  "build_up",
  "finishing",
  "defending",
  "goalkeeper",
  "effort",
  "decision",
  "other",
] as const;

export const NOTE_CATEGORY_LABELS: Record<
  (typeof NOTE_CATEGORIES)[number],
  string
> = {
  shape: "Shape",
  pressing: "Pressing",
  transition: "Transition",
  set_piece: "Set piece",
  build_up: "Build-up",
  finishing: "Finishing",
  defending: "Defending",
  goalkeeper: "Goalkeeper",
  effort: "Effort",
  decision: "Decision",
  other: "Other",
};

/** Closed category enum mirrored from the backend. */
export type NoteCategory = (typeof NOTE_CATEGORIES)[number];

/** Narrow a free `string` (from the API note payload or a
 * template) to the closed `NoteCategory` enum. Anything off-list
 * falls back to `"other"` — the same default the backend uses.
 * Used wherever a `string`-typed category crosses into the
 * composer's typed form values. */
export function coerceNoteCategory(
  value: string | null | undefined,
): NoteCategory {
  return (NOTE_CATEGORIES as readonly string[]).includes(value ?? "")
    ? (value as NoteCategory)
    : "other";
}

// One vocabulary for every composer — see visibilityVocabulary.ts.
const VISIBILITY_OPTIONS = visibilitySegmentOptions();

// Observation event type — closed enum mirrored from the backend's
// `_VALID_EVENT_TYPES` set in `models.py`. The empty string is also
// valid backend-side (event_type is optional); the composer's
// dropdown offers a "Practice" default rather than a blank, but the
// schema still permits "" so a seeded note with no event_type
// round-trips.
const EVENT_TYPES = [
  "practice",
  "game",
  "meeting",
  "tactical",
  "other",
] as const;

const EVENT_TYPE_LABELS: Record<(typeof EVENT_TYPES)[number], string> = {
  practice: "Training",
  game: "Game",
  meeting: "Meeting",
  tactical: "Tactical",
  other: "Other",
};

// `event_date` accepts empty OR `YYYY-MM-DD` — mirrors the backend's
// `_DATE_RE` / `_validate_event_date`. We use a styled text input
// (NOT `<input type="date">`) per the no-native-chrome rule, so the
// schema enforces the format the native picker would have.
const EVENT_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Zod schema for the composer. Defaults intentionally NOT declared
// on optional-text fields here — `.default()` makes the input type
// optional but output required, which trips RHF's resolver generics
// (the form values type then has `string | undefined` for fields
// the resolver expects as `string`). The form's `defaultValues`
// prop on `useForm` covers the same purpose.
//
// The `event_*` fields are always part of the schema (the form is
// one shape), but they only RENDER in observation mode — see the
// `observationMode` prop. In video mode they stay at their
// defaults and the parent ignores them.
export const composerSchema = z.object({
  title: z.string().min(1, "Title is required").max(200),
  note_type: z.enum([
    "positive",
    "correction",
    "question",
    "team_concept",
    "individual_goal",
  ]),
  category: z.enum(NOTE_CATEGORIES),
  visibility: z.enum(["private", "team", "player"]),
  // Long-form free notes — the backend's `body` column (max 4000).
  // Optional; the structured fields below are the preferred way to
  // capture feedback, but `body` is here so a coach can write
  // free-form AND so editing an existing note that has a `body`
  // doesn't silently drop it.
  body: z.string().max(4000),
  player_summary: z.string().max(2000),
  what_happened: z.string().max(2000),
  why_it_matters: z.string().max(2000),
  what_to_do_next: z.string().max(2000),
  coach_private_note: z.string().max(2000),
  tags_raw: z.string(),
  player_ids: z.array(z.string()),
  event_title: z.string().max(200),
  event_date: z.string().refine((v) => v === "" || EVENT_DATE_RE.test(v), {
    message: "Use YYYY-MM-DD or leave blank",
  }),
  event_type: z.enum(["", ...EVENT_TYPES]),
  // Reference video (0053, #675). Resolved server-side from a pasted URL —
  // the composer never constructs these itself, it stores what
  // /api/coach/reference-video/resolve returned. Empty string = no video,
  // matching the "" convention the other optional fields use here.
  ref_video_provider: z.enum(["", "youtube", "vimeo", "file"]),
  ref_video_id: z.string().max(2048),
  ref_video_label: z.string().max(200),
  ref_video_title: z.string().max(300),
  ref_video_thumb: z.string().max(500),
});

export type ComposerValues = z.infer<typeof composerSchema>;

/** Imperative handle the parent uses to read the LIVE form state.
 *
 * Why: the parent shell owns two pieces of behavior that depend on
 * current-instead-of-default values:
 *   1. TemplateSelector's overwrite-protect needs the live values to
 *      decide whether to fire a confirm dialog (a coach who typed
 *      into the title field should be asked before another template
 *      clobbers it).
 *   2. Focus-mode toggle: the inline composer unmounts while the
 *      coach is in canvas-fullscreen mode. The parent snapshots
 *      `getValues()` into `composerOverride` before the unmount so
 *      the remount on close picks up unsaved title / private note
 *      / tags / structured text.
 *
 * `reset()` is also exposed so the parent can clear the form after
 * a successful save without bumping the key (legacy "next moment
 * starts from scratch" behavior). */
export type ComposerHandle = {
  getValues: () => ComposerValues;
  isDirty: () => boolean;
  getDirtyValues: () => Partial<ComposerValues>;
  reset: (next?: Partial<ComposerValues>) => void;
};

export type ComposerProps = {
  defaultValues?: Partial<ComposerValues>;
  /** Bounded Assist approval writing only. Selection and facts belong to the parent. */
  approvalMode?: boolean;
  disabled?: boolean;
  players: Player[];
  onSubmit: SubmitHandler<ComposerValues>;
  submitLabel?: string;
  hidePlayerPicker?: boolean;
  /** When true, render the observation event fields (event title /
   * date / type). Coach Review passes `source === "tactical_board"`
   * — observations carry event metadata; video notes don't. */
  observationMode?: boolean;
  /** A4: frequency-ranked player ids (already filtered to ≤5, in
   * roster, not yet tagged) rendered as quick-add chips above the
   * "Tag players" picker. The parent computes these from the match's
   * other notes/clips via `suggestedPlayerIds`. */
  suggestedPlayerIds?: string[];
  /** Production templates are retained as a compact optional control in
   * the approved writing column. */
  templateSelector?: React.ReactNode;
  /** Drawer-level cancel action rendered beside Save. */
  onCancel?: () => void;
};

const DEFAULTS: ComposerValues = {
  title: "",
  note_type: "correction",
  category: "other",
  visibility: "team",
  body: "",
  player_summary: "",
  what_happened: "",
  why_it_matters: "",
  what_to_do_next: "",
  coach_private_note: "",
  tags_raw: "",
  player_ids: [],
  event_title: "",
  event_date: "",
  event_type: "practice",
  ref_video_provider: "",
  ref_video_id: "",
  ref_video_label: "",
  ref_video_title: "",
  ref_video_thumb: "",
};

export const Composer = forwardRef<ComposerHandle, ComposerProps>(
  function Composer(
    {
      defaultValues,
      approvalMode = false, disabled = false,
      players,
      onSubmit,
      submitLabel = "Save note",
      hidePlayerPicker = false,
      observationMode = false,
      suggestedPlayerIds = [],
      templateSelector,
      onCancel,
    }: ComposerProps,
    ref,
  ) {
    const form = useForm<ComposerValues>({
      resolver: zodResolver(composerSchema),
      defaultValues: { ...DEFAULTS, ...defaultValues },
    });
    const baselineRef = useRef<ComposerValues>({
      ...DEFAULTS,
      ...defaultValues,
    });
    const [structuredOpen, setStructuredOpen] = useState(false);

    useEffect(() => {
      // Late bundle/default-visibility hydration must not erase a field already typed.
      // A different record/template mounts with a new key; explicit reset stays destructive.
      const nextDefaults = { ...DEFAULTS, ...defaultValues };
      baselineRef.current = nextDefaults;
      form.reset(nextDefaults, { keepDirtyValues: true });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(defaultValues)]);

    // A4: smart visibility default. When the coach tags the FIRST
    // player on a note still at the "team" default they haven't touched,
    // flip visibility to "player" — a player-specific note almost always
    // wants player visibility, and re-typing it is friction. We never
    // override a visibility the coach touched (`dirtyFields.visibility`),
    // and only act on the empty→non-empty edge so re-adding a second
    // player (or clearing + re-adding) doesn't re-flip a deliberate
    //
    // Note this ALSO flips a `team` that came from the team's
    // `notes.default_visibility` setting, because `form.reset()` clears
    // dirty state and the two are indistinguishable here. That is
    // deliberate: the setting is documented as where a new note
    // *starts* ("Coaches can still change visibility on any individual
    // item"), and narrowing team→player on an explicit player tag stays
    // within the coach's own authority. A setting stricter than `player`
    // — `private` — is never touched, because the flip only fires on the
    // literal value "team".
    // choice. The `players` field watch drives the edge detection.

    const watchedPlayerIds = form.watch("player_ids");
    const prevHadPlayersRef = useRef(false);
    useEffect(() => {
      const hasPlayers = (watchedPlayerIds ?? []).length > 0;
      const wasEmpty = !prevHadPlayersRef.current;
      prevHadPlayersRef.current = hasPlayers;
      if (
        hasPlayers &&
        wasEmpty &&
        !form.formState.dirtyFields.visibility &&
        form.getValues("visibility") === "team"
      ) {
        form.setValue("visibility", "player", { shouldDirty: false });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedPlayerIds]);

    useImperativeHandle(
      ref,
      () => ({
        getValues: () => form.getValues(),
        getDirtyValues: () => Object.fromEntries(Object.keys(form.getValues()).filter(key => JSON.stringify(form.getValues(key as keyof ComposerValues)) !== JSON.stringify(baselineRef.current[key as keyof ComposerValues])).map(key => [key, form.getValues(key as keyof ComposerValues)])),
        // RHF updates formState on its next render. A close/navigation can
        // happen in the same task as an input event, so compare the live
        // values with the last reset baseline as well. This keeps drafts
        // from being dropped under a busy browser before isDirty re-renders.
        isDirty: () =>
          form.formState.isDirty ||
          JSON.stringify(form.getValues()) !== JSON.stringify(baselineRef.current),
        reset: (next?: Partial<ComposerValues>) => {
          const nextValues = { ...DEFAULTS, ...(next ?? {}) };
          baselineRef.current = nextValues;
          form.reset(nextValues);
        },
      }),
      [form],
    );

    const visibility = form.watch("visibility");
    const activeVisibility = VISIBILITY_OPTIONS.find(
      (v) => v.value === visibility,
    );
    // `watchedPlayerIds` is already watched above for the auto-flip.
    const audienceSentence = sharedWithSentence({
      visibility,
      playerIds: watchedPlayerIds ?? [],
      players,
    });

    return (
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="review-note-composer flex flex-col gap-4"
        data-testid="coach-review-composer"
      >
        {observationMode && !approvalMode ? (
          <section
            className="review-composer-event grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]"
            data-testid="composer-event-fields"
          >
            <Field label="Event title">
              <input
                type="text"
                {...form.register("event_title")}
                data-testid="composer-event-title"
                placeholder="Tuesday practice"
                className="w-full rounded border border-rule bg-canvas px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </Field>
            <Field
              label="Event date"
              error={form.formState.errors.event_date?.message}
            >
              <Controller
                control={form.control}
                name="event_date"
                render={({ field }) => (
                  <TkticDateInput
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    data-testid="composer-event-date"
                  />
                )}
              />
            </Field>
            <Field label="Event type">
              <Controller
                control={form.control}
                name="event_type"
                render={({ field }) => (
                  <TkticSelect
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    data-testid="composer-event-type"
                    options={EVENT_TYPES.map((t) => ({
                      value: t,
                      label: EVENT_TYPE_LABELS[t],
                    }))}
                  />
                )}
              />
            </Field>
          </section>
        ) : null}

        <div className="review-composer-columns grid gap-4 lg:grid-cols-2">
          <div className="review-composer-writing flex min-w-0 flex-col gap-3">
            <Field label="Title *" error={form.formState.errors.title?.message}>
              <input
                type="text"
                {...form.register("title")}
                maxLength={approvalMode ? 160 : 200}
                data-testid="composer-title"
                className="w-full rounded border border-rule bg-canvas px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </Field>

            <Field label="Player-facing summary">
              <textarea
                {...form.register("player_summary")}
                rows={3}
                data-testid="composer-player-summary"
                className="w-full rounded border border-rule bg-canvas px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent resize-y"
              />
            </Field>

            <Field
              label="Full note"
              helper="Visible to players and guardians when this note's visibility allows it."
              error={form.formState.errors.body?.message}
            >
              <textarea
                {...form.register("body")}
                rows={6}
                data-testid="composer-body"
                className="w-full rounded border border-rule bg-canvas px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent resize-y"
              />
            </Field>

            <section className="rounded border border-rule bg-surface">
              <button
                type="button"
                onClick={() => setStructuredOpen((v) => !v)}
                aria-expanded={structuredOpen}
                aria-controls="composer-structured-feedback"
                data-testid="composer-structured-toggle"
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-display uppercase tracking-wider text-ink-muted hover:text-ink transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                <span>Structured feedback</span>
                <span
                  className="inline-block transition-transform"
                  style={{
                    transform: structuredOpen
                      ? "rotate(90deg)"
                      : "rotate(0deg)",
                  }}
                  aria-hidden="true"
                >
                  ›
                </span>
              </button>
              {structuredOpen ? (
                <div
                  id="composer-structured-feedback"
                  className="grid gap-3 border-t border-rule p-3 lg:grid-cols-3"
                >
                  <Field label="What happened">
                    <textarea
                      {...form.register("what_happened")}
                      rows={4}
                      data-testid="composer-what-happened"
                      className="w-full rounded border border-rule bg-canvas px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent resize-y"
                    />
                  </Field>
                  <Field label="Why it matters">
                    <textarea
                      {...form.register("why_it_matters")}
                      rows={4}
                      data-testid="composer-why-it-matters"
                      className="w-full rounded border border-rule bg-canvas px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent resize-y"
                    />
                  </Field>
                  <Field label="What to do next">
                    <textarea
                      {...form.register("what_to_do_next")}
                      rows={4}
                      data-testid="composer-what-to-do-next"
                      className="w-full rounded border border-rule bg-canvas px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent resize-y"
                    />
                  </Field>
                </div>
              ) : null}
            </section>

            {!approvalMode ? <><Field label="Coach-only note">
              <textarea
                {...form.register("coach_private_note")}
                rows={3}
                data-testid="composer-coach-private-note"
                className="w-full rounded border border-rule bg-canvas px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent resize-y"
              />
            </Field>

            <div className="review-composer-writing-extras">
              <Field label="Tags">
                <input
                  type="text"
                  {...form.register("tags_raw")}
                  data-testid="composer-tags"
                  placeholder="Separate tags with commas"
                  className="w-full rounded border border-rule bg-canvas px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </Field>
              <ReferenceVideoField
                values={{
                  ref_video_provider: form.watch("ref_video_provider"),
                  ref_video_id: form.watch("ref_video_id"),
                  ref_video_label: form.watch("ref_video_label"),
                  ref_video_title: form.watch("ref_video_title"),
                  ref_video_thumb: form.watch("ref_video_thumb"),
                }}
                onChange={(patch) => {
                  for (const [key, value] of Object.entries(patch)) {
                    form.setValue(key as keyof ComposerValues, value as never, {
                      shouldDirty: true,
                    });
                  }
                }}
              />
            </div>

            </> : null}
            <div className="review-composer-assists">
              {templateSelector}
            </div>
          </div>

          {!approvalMode ? <aside className="review-composer-settings flex min-w-0 flex-col gap-3 rounded border border-rule bg-surface p-3 shadow-sm">
            <Field label="Tone">
              <ToneChipGroup
                value={form.watch("note_type")}
                onChange={(next) =>
                  form.setValue("note_type", next, { shouldDirty: true })
                }
                data-testid="composer-note-type"
              />
            </Field>
            {/* Collapses on mobile: a 3-segment visibility control needs
             * more than half of a narrow drawer. Mirrors the responsive
             * grid MatchSummaryComposerModal already uses. */}
            <Field label="Category">
              <Controller
                control={form.control}
                name="category"
                render={({ field }) => (
                  <TkticSelect
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    data-testid="composer-category"
                    options={NOTE_CATEGORIES.map((c) => ({
                      value: c,
                      label: NOTE_CATEGORY_LABELS[c],
                    }))}
                  />
                )}
              />
            </Field>
            <Field label="Visibility" helper={activeVisibility?.description}>
              <Controller
                control={form.control}
                name="visibility"
                render={({ field }) => (
                  <SegmentedControl
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    ariaLabel="Visibility"
                    data-testid="composer-visibility"
                    options={VISIBILITY_OPTIONS.map((v) => ({
                      value: v.value,
                      label: v.label,
                    }))}
                  />
                )}
              />
            </Field>

            {!hidePlayerPicker ? (
              <Field label="Tag players">
                {/* A4: quick-add chips for the players most-tagged on
                 * this match's other notes/clips, minus anyone already
                 * tagged here. Clicking adds them to the picker. */}
                {(() => {
                  const tagged = form.watch("player_ids") ?? [];
                  const taggedSet = new Set(tagged);
                  const chips = suggestedPlayerIds
                    .filter((id) => !taggedSet.has(id))
                    .map((id) => players.find((p) => p.id === id))
                    .filter((p): p is Player => Boolean(p));
                  if (chips.length === 0) return null;
                  return (
                    <div
                      className="mb-2 flex flex-wrap items-center gap-1.5"
                      data-testid="composer-suggested-players"
                    >
                      <span className="text-xs text-ink-muted">Suggested:</span>
                      {chips.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() =>
                            form.setValue(
                              "player_ids",
                              [...(form.getValues("player_ids") ?? []), p.id],
                              { shouldDirty: true },
                            )
                          }
                          data-testid={`composer-suggested-player-${p.id}`}
                          className="inline-flex items-center gap-1 rounded-full border border-rule px-2 py-0.5 text-xs text-ink transition-colors hover:bg-field"
                        >
                          <PlusIcon size={11} strokeWidth={2.5} />
                          {p.display_name}
                          {p.jersey_number ? ` · #${p.jersey_number}` : ""}
                        </button>
                      ))}
                    </div>
                  );
                })()}
                <ComposerPlayerPicker
                  value={form.watch("player_ids") ?? []}
                  onChange={(next) =>
                    form.setValue("player_ids", next, { shouldDirty: true })
                  }
                  players={players}
                />
              </Field>
            ) : null}

          </aside> : null}
        </div>

        {!observationMode && !approvalMode ? (
          <div className="review-composer-audience" data-testid="composer-audience">
            {audienceSentence ? (
              <p className="text-xs text-ink-muted">
                <span className="font-display text-ink">Shared with:</span>{" "}
                {audienceSentence}
              </p>
            ) : (
              <p
                className="rounded-inset border border-flag/40 bg-flag/10 px-2 py-1.5 text-xs text-flag"
                data-testid="composer-audience-warning"
              >
                {NO_RECIPIENTS_WARNING}
              </p>
            )}
          </div>
        ) : null}
        <div className="review-composer-actions">
          {onCancel ? (
            <button type="button" onClick={onCancel} className="review-composer-cancel">
              Cancel
            </button>
          ) : null}
          <button
            type="submit"
            data-testid="composer-submit"
            disabled={disabled || form.formState.isSubmitting}
            className="review-composer-submit rounded bg-accent px-4 py-2 text-sm font-display text-accent-ink transition-colors hover:bg-accent-hover disabled:bg-field disabled:text-ink-muted"
          >
            {form.formState.isSubmitting ? "Saving…" : submitLabel}
          </button>
        </div>
      </form>
    );
  },
);

export function ComposerPlayerPicker({
  players,
  value,
  onChange,
  testId = "composer-player-ids-picker",
}: {
  players: Player[];
  value: string[];
  onChange: (next: string[]) => void;
  testId?: string;
}) {
  const collection = useCollection(players, {
    scope: "composer-player-picker",
    text: (player) =>
      `${player.display_name} ${player.jersey_number ?? ""} ${player.position ?? ""}`,
    matches: (player, status) =>
      status === "active" ? player.active : !player.active,
  });
  const selected = new Set(value);

  return (
    <div
      className="flex flex-col gap-2"
      data-testid={testId}
    >
      <CollectionToolbar
        collection={collection}
        label="players"
        searchPlaceholder="Name, jersey or position"
        filterLabel="Status"
        filters={[
          { value: "", label: "All players" },
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ]}
      />
      <div className="grid grid-cols-1 gap-x-3 gap-y-1 sm:grid-cols-2">
        {collection.items.map((player) => {
          const checked = selected.has(player.id);
          return (
            <label
              key={player.id}
              className="flex min-h-9 cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-ink transition-colors hover:bg-field"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() =>
                  onChange(
                    checked
                      ? value.filter((id) => id !== player.id)
                      : [...value, player.id],
                  )
                }
                className="peer sr-only"
                data-testid={`composer-player-${player.id}`}
              />
              <span
                aria-hidden="true"
                className="grid size-5 shrink-0 place-items-center rounded border border-rule bg-canvas text-xs text-accent peer-focus-visible:ring-2 peer-focus-visible:ring-accent"
              >
                {checked ? "✓" : ""}
              </span>
              <span className="min-w-0 truncate">
                {player.jersey_number ? `#${player.jersey_number} ` : ""}
                {player.display_name}
              </span>
            </label>
          );
        })}
      </div>
      <CollectionPagination collection={collection} label="players" />
    </div>
  );
}

/** Field wrapper — label + optional helper text + error display. */
function Field(props: {
  label: string;
  helper?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
        {props.label}
      </span>
      {props.children}
      {props.helper ? (
        <span className="text-xs text-ink-muted">{props.helper}</span>
      ) : null}
      {props.error ? (
        <span className="text-xs text-destructive">{props.error}</span>
      ) : null}
    </label>
  );
}

/** Parse the comma-separated tags input into a clean string[]. */
export function parseTagsRaw(raw: string): string[] {
  return raw
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}
